import React, { useMemo, useState } from "react"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import HighchartsMore from "highcharts/highcharts-more"
import SolidGauge from "highcharts/modules/solid-gauge"
import Treemap from "highcharts/modules/treemap"

// Initialize modules once at module load
HighchartsMore(Highcharts)
SolidGauge(Highcharts)
Treemap(Highcharts)

const CHART_COLORS = {
  teal: "#407775",
  lightTeal: "#6F9896",
  gold: "#FAB430",
  softGreen: "#8FBC8F",
  coral: "#E8927C",
  slate: "#708090",
  water: "#5B9BD5",
  notReported: "#D0D0D0",
}

const commonChartConfig = {
  credits: { enabled: false },
  chart: { backgroundColor: "transparent", style: { fontFamily: "inherit" } },
  title: {
    style: { fontSize: "12px", fontWeight: "600", color: "#494949" },
    align: "left",
  },
}

const IRO_COLORS = {
  Narrative: "#B4A34A",
  Matrix: "#FAB430",
  "Matrix + Flowchart": "#6F9896",
  "Narrative + Diagram": "#3F7573",
  None: "#999999",
}

const ChartGrid = ({ chartData, dataset = "csrd" }) => {
  const showCsrdSpecific = dataset !== "nifty"
  const isNiftyLike = dataset === "nifty" || dataset === "all"
  const [logScale, setLogScale] = useState(false)

  const chartOptions = useMemo(() => {
    // Compute a small sentinel value for "Not Reported" scope 3 bars
    const allScope12 = (chartData.emissions_profile || []).flatMap((d) => [d.scope1 ?? 0, d.scope2 ?? 0])
    const maxScope12 = Math.max(...allScope12, 1)
    const notReportedIndicator = maxScope12 * 0.08
    const anyMissingScope3 = (chartData.emissions_profile || []).some((d) => d.scope3 == null)

    const emissionsOptions = {
      ...commonChartConfig,
      chart: { ...commonChartConfig.chart, type: "bar", height: 260 },
      title: { ...commonChartConfig.title, text: "Emissions Profile (tCO2e)" },
      xAxis: {
        categories: chartData.emissions_profile?.map((d) => d.company) || [],
        labels: { style: { fontSize: "10px" } },
      },
      yAxis: {
        type: logScale ? "logarithmic" : "linear",
        title: { text: "" },
        labels: { style: { fontSize: "10px" }, formatter: function () { return Highcharts.numberFormat(this.value, 0, ".", ",") } },
        tickPixelInterval: 80,
      },
      legend: { enabled: true, itemStyle: { fontSize: "10px" } },
      plotOptions: { bar: { borderRadius: 3, groupPadding: 0.15 } },
      tooltip: {
        formatter: function () {
          if (this.series.name === "Scope 3 (Not Reported)") {
            return `<b>${this.x}</b><br/><span style="color:${CHART_COLORS.notReported}">●</span> Scope 3: <b>Not reported</b>`
          }
          return `<b>${this.x}</b><br/><span style="color:${this.series.color}">●</span> ${this.series.name}: <b>${Highcharts.numberFormat(this.y, 0)}</b>`
        },
      },
      series: [
        { name: "Scope 1", data: chartData.emissions_profile?.map((d) => d.scope1 ?? null) || [], color: CHART_COLORS.teal },
        { name: "Scope 2", data: chartData.emissions_profile?.map((d) => d.scope2 ?? null) || [], color: CHART_COLORS.gold },
        { name: "Scope 3", data: chartData.emissions_profile?.map((d) => d.scope3 ?? null) || [], color: CHART_COLORS.lightTeal },
        ...(anyMissingScope3 ? [{
          name: "Scope 3 (Not Reported)",
          data: chartData.emissions_profile?.map((d) => d.scope3 == null ? notReportedIndicator : null) || [],
          color: CHART_COLORS.notReported,
          borderColor: "#999",
          borderWidth: 1,
        }] : []),
      ],
    }

    const countryOptions = {
      ...commonChartConfig,
      chart: { ...commonChartConfig.chart, type: "column", height: 260 },
      title: { ...commonChartConfig.title, text: "Companies by Country" },
      xAxis: {
        categories: chartData.companies_by_country?.map((d) => d.country) || [],
        labels: { style: { fontSize: "10px" }, rotation: -45 },
      },
      yAxis: { min: 0, title: { text: "" }, allowDecimals: false },
      legend: { enabled: false },
      plotOptions: { column: { borderRadius: 4 } },
      series: [
        {
          name: "Companies",
          data: chartData.companies_by_country?.map((d) => d.count) || [],
          color: CHART_COLORS.gold,
        },
      ],
    }

    const diversityOptions = {
      ...commonChartConfig,
      chart: { ...commonChartConfig.chart, type: "bar", height: 260, marginRight: 40 },
      title: { ...commonChartConfig.title, text: "Workforce & Board Diversity (% Female)" },
      xAxis: {
        categories: chartData.workforce_and_board_diversity?.map((d) => d.company) || [],
        labels: { style: { fontSize: "10px" } },
      },
      yAxis: { min: 0, max: 100, title: { text: "" }, labels: { format: "{value}%", overflow: "allow" } },
      legend: { enabled: true, itemStyle: { fontSize: "10px" } },
      plotOptions: { bar: { borderRadius: 3 } },
      tooltip: {
        formatter: function () {
          if (this.y == null) return `<b>${this.x}</b><br/><span style="color:${this.series.color}">●</span> ${this.series.name}: <b>Not reported</b>`
          return `<b>${this.x}</b><br/><span style="color:${this.series.color}">●</span> ${this.series.name}: <b>${this.y.toFixed(1)}%</b>`
        },
      },
      series: [
        {
          name: "Board",
          data: chartData.workforce_and_board_diversity?.map((d) => d.board_female_pct ?? null) || [],
          color: CHART_COLORS.teal,
          dataLabels: {
            enabled: true,
            format: "{y:.0f}%",
            style: { fontSize: "10px", fontWeight: "500", textOutline: "none", color: "#494949" },
            inside: false,
            align: "left",
          },
        },
        {
          name: "Workforce",
          data: chartData.workforce_and_board_diversity?.map((d) => d.workforce_female_pct ?? null) || [],
          color: CHART_COLORS.lightTeal,
          dataLabels: {
            enabled: true,
            format: "{y:.0f}%",
            style: { fontSize: "10px", fontWeight: "500", textOutline: "none", color: "#494949" },
            inside: false,
            align: "left",
          },
        },
      ],
    }

    // EU Taxonomy gauge
    const euTaxonomyOptions = {
      ...commonChartConfig,
      chart: { ...commonChartConfig.chart, type: "solidgauge", height: 260 },
      title: { ...commonChartConfig.title, text: "EU Taxonomy Disclosure" },
      pane: {
        center: ["50%", "65%"],
        size: "90%",
        startAngle: -90,
        endAngle: 90,
        background: {
          backgroundColor: "#E0E0E0",
          innerRadius: "60%",
          outerRadius: "100%",
          shape: "arc",
        },
      },
      tooltip: { enabled: false },
      yAxis: {
        min: 0,
        max: 100,
        stops: [[1, CHART_COLORS.teal]],
        lineWidth: 0,
        tickWidth: 0,
        minorTickInterval: null,
        tickAmount: 2,
        labels: { y: 16, style: { fontSize: "10px" } },
      },
      plotOptions: {
        solidgauge: {
          dataLabels: {
            y: -30,
            borderWidth: 0,
            useHTML: true,
            format:
              '<div style="text-align:center"><span style="font-size:22px;font-weight:700;color:#494949">{y}%</span></div>',
          },
        },
      },
      series: [{ name: "EU Taxonomy", data: [chartData.eu_taxonomy_percent || 0] }],
    }

    // IRO Visual Format treemap
    const iroData = chartData.iro_visual_format || {}
    const iroTreemapData = Object.entries(typeof iroData === "object" && !Array.isArray(iroData) ? iroData : {})
      .filter(([, value]) => value > 0)
      .map(([name, value]) => ({
        name: `${name} (${value})`,
        value,
        color: IRO_COLORS[name] || "#999999",
      }))

    const iroOptions = {
      ...commonChartConfig,
      chart: { ...commonChartConfig.chart, type: "treemap", height: 260 },
      title: { ...commonChartConfig.title, text: "IRO Visual Format" },
      series: [
        {
          type: "treemap",
          layoutAlgorithm: "squarified",
          data: iroTreemapData.length > 0 ? iroTreemapData : [{ name: "No data", value: 1, color: "#E0E0E0" }],
          dataLabels: {
            enabled: true,
            style: { fontSize: "11px", fontWeight: "500", textOutline: "none" },
          },
        },
      ],
      tooltip: { pointFormat: "<b>{point.name}</b>" },
    }

    // Water & Waste (dual-axis horizontal bars)
    const waterWasteOptions = {
      ...commonChartConfig,
      chart: { ...commonChartConfig.chart, type: "bar", height: 260 },
      title: { ...commonChartConfig.title, text: "Water (m³) & Waste (tonnes)" },
      xAxis: {
        categories: chartData.water_waste_profile?.map((d) => d.company) || [],
        labels: { style: { fontSize: "10px" } },
      },
      yAxis: [
        { title: { text: "Water (m³)", style: { fontSize: "10px" } }, labels: { style: { fontSize: "10px" } } },
        { title: { text: "Waste (t)", style: { fontSize: "10px" } }, opposite: true, labels: { style: { fontSize: "10px" } } },
      ],
      legend: { enabled: true, itemStyle: { fontSize: "10px" } },
      plotOptions: { bar: { borderRadius: 3, groupPadding: 0.15 } },
      tooltip: {
        shared: false,
        pointFormatter: function () {
          const unit = this.series.yAxis.index === 0 ? "m³" : "t"
          return `<span style="color:${this.series.color}">●</span> ${this.series.name}: <b>${Highcharts.numberFormat(this.y, 0)} ${unit}</b><br/>`
        },
      },
      series: [
        {
          name: "Water (m³)",
          data: chartData.water_waste_profile?.map((d) => d.water_m3 ?? null) || [],
          color: CHART_COLORS.water,
          yAxis: 0,
        },
        {
          name: "Waste (t)",
          data: chartData.water_waste_profile?.map((d) => d.waste_tonnes ?? null) || [],
          color: CHART_COLORS.coral,
          yAxis: 1,
        },
      ],
    }

    return { emissionsOptions, countryOptions, diversityOptions, euTaxonomyOptions, iroOptions, waterWasteOptions }
  }, [chartData, dataset, logScale])

  const { emissionsOptions, countryOptions, diversityOptions, euTaxonomyOptions, iroOptions, waterWasteOptions } = chartOptions

  const chartCardStyle = {
    border: "1px solid #E5E5E5",
    borderRadius: "10px",
    padding: "12px",
    backgroundColor: "#fff",
  }

  const hasWaterWaste = (chartData.water_waste_profile || []).length > 0

  return (
    <div style={{ marginBottom: "16px" }}>
      {/* Top row: emissions + country (country omitted for Nifty — all companies return India) */}
      <div style={{ display: "grid", gridTemplateColumns: showCsrdSpecific ? "1fr 1fr" : "1fr", gap: "12px", marginBottom: "12px" }}>
        <div style={chartCardStyle}>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "4px" }}>
            <button
              onClick={() => setLogScale((v) => !v)}
              style={{
                fontSize: "10px",
                padding: "3px 8px",
                borderRadius: "4px",
                border: `1px solid ${logScale ? "#407775" : "#D0D0D0"}`,
                backgroundColor: logScale ? "#407775" : "transparent",
                color: logScale ? "#fff" : "#707070",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              Log scale
            </button>
          </div>
          <HighchartsReact highcharts={Highcharts} options={emissionsOptions} />
        </div>
        {showCsrdSpecific && (
          <div style={chartCardStyle}>
            <HighchartsReact highcharts={Highcharts} options={countryOptions} />
          </div>
        )}
      </div>

      {/* Middle row: diversity + EU Taxonomy (CSRD only) | diversity + water/waste (nifty, when data exists) */}
      {(() => {
        const showSecondPanel = showCsrdSpecific || hasWaterWaste
        return (
          <div style={{ display: "grid", gridTemplateColumns: showSecondPanel ? "1fr 1fr" : "1fr", gap: "12px", marginBottom: "12px" }}>
            <div style={chartCardStyle}>
              <HighchartsReact highcharts={Highcharts} options={diversityOptions} />
            </div>
            {showCsrdSpecific && (
              <div style={chartCardStyle}>
                <HighchartsReact highcharts={Highcharts} options={euTaxonomyOptions} />
              </div>
            )}
            {!showCsrdSpecific && hasWaterWaste && (
              <div style={chartCardStyle}>
                <HighchartsReact highcharts={Highcharts} options={waterWasteOptions} />
              </div>
            )}
          </div>
        )
      })()}

      {/* Bottom: IRO treemap (CSRD only) | water/waste standalone for nifty if diversity row was full */}
      {showCsrdSpecific && (
        <div style={chartCardStyle}>
          <HighchartsReact highcharts={Highcharts} options={iroOptions} />
        </div>
      )}
    </div>
  )
}

export default ChartGrid
